import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_aa4f8546-5d46-42d2-8104-f84919deefe1/artifacts/prtdjqf5_NWP%20white.png";

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/me")
      .then((res) => {
        if (mounted && res.data?.authenticated) navigate("/admin", { replace: true });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/admin/login", { username, password });
      // Post-login sanity check — verifies the admin cookie actually landed
      // (Safari ITP / Vivaldi tracking protection can silently drop cookies).
      const meResp = await api.get("/admin/me");
      if (!meResp?.data?.authenticated) {
        // eslint-disable-next-line no-throw-literal
        throw { response: { status: 0, data: { detail: "cookie_blocked" } } };
      }
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (detail === "cookie_blocked") {
        msg = "Anmeldung nicht möglich. Bitte aktivieren Sie Cookies in Ihrem Browser oder deaktivieren Sie den Tracking-Schutz für diese Seite.";
      } else if (status === 429) {
        msg = "Zu viele Versuche. Bitte später erneut probieren.";
      } else {
        msg = "Ungültige Zugangsdaten";
      }
      // eslint-disable-next-line no-console
      console.error("[admin-login] failed", { status, code: err?.code, detail });
      setError(msg);
      setShakeKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
    >
      <div
        key={shakeKey}
        data-testid="admin-login-card"
        className={`nwp-card nwp-fade-up w-full max-w-md p-8 sm:p-10 ${error ? "nwp-shake" : ""}`}
        style={{
          background: "linear-gradient(180deg, rgba(22,22,22,0.92) 0%, rgba(14,14,14,0.92) 100%)",
          backdropFilter: "blur(6px)",
        }}
      >
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_URL}
            alt="NWP"
            data-testid="nwp-logo"
            className="h-12 w-auto mb-3"
            style={{ objectFit: "contain" }}
          />
          <div
            data-testid="admin-eyebrow"
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-2"
            style={{
              background: "rgba(225,6,0,0.10)",
              border: "1px solid rgba(225,6,0,0.35)",
              color: "#ff5a55",
              fontSize: 11,
              letterSpacing: "0.22em",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            <ShieldAlert size={13} />
            <span>Admin</span>
          </div>
          <p className="text-sm" style={{ color: "var(--nwp-text-muted)" }}>
            Geschützter Verwaltungsbereich
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs mb-2 block uppercase" style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}>
              Benutzername
            </label>
            <Input
              type="text"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              className="nwp-input h-11 text-base"
              placeholder="Username"
            />
          </div>
          <div>
            <label className="text-xs mb-2 block uppercase" style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}>
              Passwort
            </label>
            <Input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="nwp-input h-11 text-base"
              placeholder="Password"
            />
            {error && (
              <div data-testid="admin-login-error" className="text-xs mt-2" style={{ color: "#ff5a55" }}>
                {error}
              </div>
            )}
          </div>

          <button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={submitting || !username || !password}
            className="nwp-btn-primary w-full rounded-xl font-medium mt-2"
            style={{ height: 48, fontSize: 15 }}
          >
            {submitting ? "Anmelden…" : "Anmelden"}
          </button>
        </form>
      </div>

      <footer className="mt-10 text-center" data-testid="admin-login-footer">
        <div className="text-xs" style={{ color: "#6b6b6b" }}>
          © NWP — Admin Panel
        </div>
      </footer>
    </div>
  );
}
